// ---------------------------------------------------------------------------
// WHERE THE PROVIDER SENDS THE BROWSER BACK.
//
// This screen holds no decision. It reads `code` and `state` out of the URL,
// posts them once, and shows what the API said — every check that matters
// (the handshake cookie, the state match, the token signature, the nonce, the
// verified email) happens on the server, because a check in a browser is a
// suggestion.
//
// ★ IT POSTS EXACTLY ONCE. A handshake is single use and the database settles
// the race, so a second post is refused — but React 18 mounts an effect twice in
// development and the person would see that refusal rather than their console.
// The ref is what makes the effect idempotent; a dependency array cannot.
// ---------------------------------------------------------------------------
import { BrandMark } from "../design/BrandMark";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { requests, send } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { homePathFor } from "../session/access";
import { SURFACE_PREFIX } from "../system/navigation";

/** S75: nobody is known until the provider answers, so the browser's language. */
export function OidcCallback() {
  return (
    <PreSessionLocale>
      <OidcCallbackScreen />
    </PreSessionLocale>
  );
}

function OidcCallbackScreen() {
  const { t, refusal } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { apiBase, reload } = useSession();
  const [failed, setFailed] = useState<string | null>(null);
  const posted = useRef(false);

  useEffect(() => {
    if (posted.current) return;
    posted.current = true;
    const code = params.get("code");
    const state = params.get("state");
    const provider = params.get("provider") ?? window.sessionStorage.getItem("oidc.provider") ?? "";
    // The provider may also refuse — `error=access_denied` when somebody presses
    // cancel. That is not a failure to report as one.
    if (params.get("error") !== null) {
      navigate("/sign-in", { replace: true });
      return;
    }
    if (code === null || state === null || provider === "") {
      setFailed(t("That sign-in link is incomplete. Please start again."));
      return;
    }
    void (async () => {
      try {
        const res = await send<{ active_org_id: string | null; error?: { message: string } }>(
          requests.oidcCallback(apiBase, { provider, code, state }),
        );
        if (res.status !== 200) {
          setFailed(refusal(res.body?.error) ?? t("We could not sign you in with that account."));
          return;
        }
        window.sessionStorage.removeItem("oidc.provider");
        // `reload` returns what it read, so this does not fetch /v1/me twice —
        // and the fallback is SignIn's, because arriving somewhere sensible
        // matters more than agreeing with a surface this screen cannot read.
        const me = await reload();
        navigate((me && homePathFor(me)) ?? SURFACE_PREFIX.console, { replace: true });
      } catch {
        setFailed(t("We could not reach Verbunk. Please try again."));
      }
    })();
  }, [apiBase, params, navigate, reload, t]);

  return (
    <main className="centre-page">
      <BrandMark />
      <div className="centre-card">
        {failed === null ? (
          <>
            <h1>{t("Signing you in")}</h1>
            <p className="muted">{t("One moment.")}</p>
          </>
        ) : (
          <>
            <StatusChip tone="failed" label={t("Not signed in")} />
            <h1>{t("That did not work")}</h1>
            <p>{failed}</p>
            <button className="btn btn-block" type="button" onClick={() => navigate("/sign-in")}>
              {t("Back to sign in")}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
